import { hoverTooltip } from '@adornis/popover/hover-tooltip.js';
import { StatusBadge } from '@martinus/styles/client/StatusBadge.js';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { type EntraComparison, formatValue, isInSync, outOfSyncRows } from '../api/entraComparison.js';

/**
 * One labelled value the Microsoft tab compares. The comparison itself lives in
 * `api/entraComparison.ts`; this module only decides how the result looks.
 */
export interface EntraComparisonRow extends EntraComparison {
  label: string;
}

/**
 * The summary shown in the card header. Says how many values differ rather than only that something
 * does, so the header and the rows below it can never tell different stories.
 * @param rows every compared value
 * @param loading whether the snapshot is still being fetched
 */
export const EntraSyncBadge = (rows: EntraComparisonRow[], loading: boolean) => {
  if (loading) return StatusBadge({ tone: 'neutral', label: 'Wird geladen …' });

  const differences = outOfSyncRows(rows).length;

  if (!differences)
    return StatusBadge({
      tone: 'success',
      label: 'Synchron',
      iconClass: 'icon-[material-symbols--check-circle-rounded]',
    });

  return StatusBadge({
    tone: 'error',
    label: differences === 1 ? '1 Abweichung' : `${differences} Abweichungen`,
    iconClass: 'icon-[material-symbols--error-outline-rounded]',
  });
};

/**
 * The comparison table: label, what Entra holds, and whether that matches us.
 *
 * A differing row explains itself in place - the tooltip names both values - instead of leaving the
 * user to guess which of the two sides is wrong.
 * @param rows every compared value
 * @param loading whether the snapshot is still being fetched
 */
export const EntraComparisonTable = (rows: EntraComparisonRow[], loading: boolean) => html`
  <dl class="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_auto] items-center gap-x-3 gap-y-2">
    ${repeat(
      rows,
      row => row.label,
      row => {
        const same = row.informational || isInSync(row.local, row.entra);

        return html`
          <dt class="text-sm font-semibold text-base-content/70">${row.label}</dt>
          <dd class="min-w-0 ${same ? 'text-base-content' : 'text-error'}">${formatValue(row.entra)}</dd>
          <div class="flex justify-end">
            ${
              loading
                ? html`<span class="loading loading-spinner loading-xs text-base-content/70"></span>`
                : row.informational
                  ? html`<span
                      class="icon-[material-symbols--info-outline-rounded] text-lg text-base-content/70"
                      title="Wird von Exchange verwaltet und nur angezeigt"
                    ></span>`
                  : same
                    ? html`<span
                        class="icon-[material-symbols--check-circle-rounded] text-lg text-success"
                        title="Stimmt mit dem Portal überein"
                      ></span>`
                    : html`
                        <div
                          class="tooltip tooltip-left"
                          data-tip="Im Portal: ${formatValue(row.local)} — In Entra: ${formatValue(row.entra)}"
                          ${hoverTooltip('left')}
                        >
                          <span class="icon-[material-symbols--error-outline-rounded] text-lg text-error"></span>
                        </div>
                      `
            }
          </div>
        `;
      },
    )}
  </dl>
  ${rows.length ? nothing : html`<p class="text-sm text-base-content/70">Keine Werte zum Vergleichen.</p>`}
`;
